import type { LinearRGB } from '../../core/color';
import type { Vec3 } from '../../geo/types';
import type { MeshBuilder } from '../mesh/meshBuilder';
import type { ShipTemplate, SailState } from './shipTemplates';
import { hullStations } from './hull';
import { isFormed, type FormedTemplate } from './shipForm';
import { BOWSPRIT, BRACE, RIG_SPAN, YARD, mastAt, rigStylesFor, staffTopOf } from './rigParts';
import { SHEET, buildSpritRig, spritStaffHeight } from './spritRig';
import { formedPennantObstacles, formedStaffTops } from './shipMasts';
import { buildRunningRig } from './shipYards';
import { staffObstacles, type PennantObstacle } from './pennant';

/*
 * What a ship's rig is, whatever its kind: the painting's small craft set a sprit rig on one mast;
 * the big ships carry square and lateen sails on the masts of their formed hulls (shipMasts.ts,
 * shipYards.ts). A square rig needs a formed hull to stand on.
 */

function formedRig(template: ShipTemplate): FormedTemplate {
  if (!isFormed(template))
    throw new Error(`${template.type}: a square rig stands on a formed hull`);
  return template;
}

/** How far set sails swing to the lee: yards braced 35-40 degrees, sprit sails sheeted 10-20. */
export function sailSwing(template: ShipTemplate): { readonly min: number; readonly max: number } {
  return template.spritRigged ? SHEET : BRACE;
}

/**
 * How far the rig reaches in plan beyond the hull as ships are placed (bow at -X): ahead of the
 * stem to the end of the bowsprit, and to either side of the centreline 0.6 beams and a spar's
 * radius, as far as the sails of a sprit rig sheeted out furthest or a formed ship's furled
 * course yards as the painter view draws them (shipFootprint.ts).
 */
export function rigReach(template: ShipTemplate): {
  readonly beyondStem: number;
  readonly halfWidth: number;
} {
  if (template.masts.length === 0) return { beyondStem: 0, halfWidth: 0 };
  const tipRadius = BOWSPRIT.radius * BOWSPRIT.taper;
  const bowsprit =
    template.masts.length > 1
      ? BOWSPRIT.reach * template.length * Math.cos(BOWSPRIT.rise) - BOWSPRIT.heelInset + tipRadius
      : 0;
  return {
    beyondStem: Math.max(0, bowsprit),
    halfWidth: (RIG_SPAN * template.beam) / 2 + YARD.radius,
  };
}

/** The top of every pennant staff, on its mast's axis. */
export function staffTops(template: ShipTemplate): readonly Vec3[] {
  if (!template.spritRigged) return formedStaffTops(formedRig(template));
  const stations = hullStations(template);
  return template.masts.map((config) => {
    const mast = mastAt(stations, config);
    return staffTopOf(mast, spritStaffHeight(mast));
  });
}

/** The spars and ropes a pennant must end short of. */
export function pennantObstacles(template: ShipTemplate): readonly PennantObstacle[] {
  if (!template.spritRigged) return formedPennantObstacles(formedRig(template));
  return staffObstacles(staffTops(template));
}

/**
 * The rig as it changes with the sails (bow at -X): a sprit rig whole; the yards and sails of a
 * formed ship, whose masts and standing rigging stand in its body. Every spar, rope and furled
 * sail is a closed solid and set sails are double-sided cloth. `brace` swings every set sail
 * about its mast by the same angle, so all masts are on one tack.
 */
export function buildRigging(
  builder: MeshBuilder,
  template: ShipTemplate,
  sailState: SailState,
  riggingColor: LinearRGB,
  sailColor: LinearRGB,
  edgeId: number,
  seed: number,
  brace = 0
): void {
  if (template.masts.length === 0) return;
  const styles = rigStylesFor(riggingColor, sailColor, edgeId, seed);
  if (template.spritRigged) buildSpritRig(builder, styles, template, sailState, brace);
  else buildRunningRig(builder, formedRig(template), sailState, brace, styles);
}
